import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from "components/mui";
import DownloadIcon from "@mui/icons-material/Download";
import { createAttachmentDownloadUrl } from "libs/utils/fileUtils";

function AttachmentPreviewModal({ open, onClose, attachment }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Reset when the modal closes.
    if (!open) {
      setPreviewUrl(null);
      setLoading(true);
      setError(null);
      return undefined;
    }

    // Nothing to render a preview from. This case previously matched no branch at all,
    // so `loading` stayed true and the modal showed a spinner forever instead of a
    // message.
    if (!attachment || !attachment.data) {
      setPreviewUrl(null);
      setLoading(false);
      setError(attachment ? "This attachment has no data to preview." : null);
      return undefined;
    }

    let blobUrl = null;
    try {
      // Convert base64 to a blob URL for preview
      blobUrl = createAttachmentDownloadUrl(attachment);
      if (blobUrl) {
        setPreviewUrl(blobUrl);
        setError(null);
      } else {
        setPreviewUrl(null);
        setError("Failed to load preview");
      }
    } catch (err) {
      console.error("[AttachmentPreviewModal] Error creating preview:", err);
      setPreviewUrl(null);
      setError("Failed to load preview");
    }
    setLoading(false);

    // Scoped to the URL this run created, so swapping attachments while the modal stays
    // open releases the previous blob before the next one is shown.
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [open, attachment]);

  const handleDownload = () => {
    if (previewUrl && attachment) {
      const link = document.createElement("a");
      link.href = previewUrl;
      link.download = attachment.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const renderPreview = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      );
    }

    if (!attachment) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <Typography variant="body1" color="text.secondary">
            No attachment selected
          </Typography>
        </Box>
      );
    }

    // Preview based on file type
    if (attachment.type && attachment.type.startsWith("image/")) {
      return (
        <Box sx={{ textAlign: "center" }}>
          <img
            src={previewUrl}
            alt={attachment.name}
            style={{ maxWidth: "100%", maxHeight: "500px", objectFit: "contain" }}
          />
        </Box>
      );
    } else if (attachment.type === "application/pdf") {
      return (
        <iframe
          src={previewUrl}
          style={{ width: "100%", height: "500px", border: "none" }}
          title={attachment.name}
        />
      );
    } else {
      return (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Preview not available for this file type.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            File: {attachment.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Type: {attachment.type || "Unknown"}
          </Typography>
        </Box>
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Preview: {attachment?.name || "Attachment"}
      </DialogTitle>
      <DialogContent>{renderPreview()}</DialogContent>
      <DialogActions>
        <Button onClick={handleDownload} startIcon={<DownloadIcon />} color="primary">
          Download
        </Button>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AttachmentPreviewModal;
